import React, { useState } from "react";
import "./LandingPage.css";

const TENNIS_APP_URL = "https://tennis-uploader.web.app";

const processSteps = [
  ["01", "Capture the movement", "Record a focused practice clip with the motion fully in frame."],
  ["02", "See the moments", "Review clear, camera-view evidence at the points that matter most."],
  ["03", "Build the next rep", "Turn one understandable coaching cue into a more intentional practice."],
];

function ArrowIcon() {
  return <span aria-hidden="true">→</span>;
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="landing-page">
      <header className="landing-nav">
        <a className="landing-brand" href="#top" onClick={closeMenu} aria-label="Evolve Sports home">
          <span className="landing-mark">e</span>
          <span>Evolve <b>Sports</b></span>
        </a>
        <button
          type="button"
          className="landing-menu-button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="landing-navigation"
        >
          {menuOpen ? "Close" : "Menu"}
        </button>
        <nav id="landing-navigation" className={menuOpen ? "landing-links is-open" : "landing-links"}>
          <a href="#approach" onClick={closeMenu}>Approach</a>
          <a href="#sports" onClick={closeMenu}>Sports</a>
          <a href="#tennis" onClick={closeMenu}>Tennis</a>
          <a className="landing-nav-cta" href={TENNIS_APP_URL}>Open tennis app <ArrowIcon /></a>
        </nav>
      </header>

      <main id="top">
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <p className="landing-eyebrow">Motion intelligence for intentional athletes</p>
            <h1>See the work.<br /><em>Shape what’s next.</em></h1>
            <p className="landing-lede">
              Evolve Sports turns practice video into clear, useful visual feedback—starting
              with tennis and growing toward a better way to learn across sport.
            </p>
            <div className="landing-actions">
              <a className="landing-button landing-button-primary" href="#tennis">
                Explore tennis <ArrowIcon />
              </a>
              <a className="landing-button landing-button-secondary" href="#approach">
                Our approach
              </a>
            </div>
          </div>
          <div className="landing-hero-art" aria-label="Abstract motion study">
            <div className="landing-orbit landing-orbit-one" />
            <div className="landing-orbit landing-orbit-two" />
            <div className="landing-motion-line line-one" />
            <div className="landing-motion-line line-two" />
            <div className="landing-motion-line line-three" />
            <div className="landing-motion-point point-one" />
            <div className="landing-motion-point point-two" />
            <div className="landing-motion-point point-three" />
            <p>Practice<br />in motion</p>
          </div>
        </section>

        <section id="approach" className="landing-section landing-process">
          <div className="landing-section-intro">
            <p className="landing-eyebrow">The Evolve method</p>
            <h2>A quieter way to<br /><em>train with purpose.</em></h2>
            <p>
              We believe useful technology makes the next decision simpler. Our experiences
              focus on visible movement, practical coaching context, and progress built over
              consistent practice.
            </p>
          </div>
          <div className="landing-steps">
            {processSteps.map(([number, title, description]) => (
              <article className="landing-step" key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="sports" className="landing-section landing-sports">
          <div>
            <p className="landing-eyebrow">One platform, more possibilities</p>
            <h2>Built to evolve<br /><em>beyond one sport.</em></h2>
          </div>
          <p className="landing-sports-copy">
            Tennis is where we are learning first. The Evolve platform is being shaped for
            athletes, families, and coaches who want a more thoughtful way to understand
            practice across sports.
          </p>
          <div className="landing-sport-grid">
            <article className="landing-sport-card landing-sport-card-active">
              <span>01</span><h3>Tennis</h3><p>Available now</p>
            </article>
            <article className="landing-sport-card"><span>02</span><h3>More sports</h3><p>In exploration</p></article>
            <article className="landing-sport-card"><span>03</span><h3>Coach tools</h3><p>Planned</p></article>
          </div>
        </section>

        <section id="tennis" className="landing-section landing-tennis">
          <div>
            <p className="landing-eyebrow">Available now · Tennis</p>
            <h2>Build a smarter<br /><em>next serve.</em></h2>
          </div>
          <div className="landing-tennis-details">
            <p>
              Upload one serve and receive camera-view visual analysis, an understandable
              coaching focus, and a simple way to compare the reps you build over time.
            </p>
            <ul>
              <li>Five free completed serve analyses</li>
              <li>Private to your account</li>
              <li>Camera-view estimates, not medical advice</li>
            </ul>
            <a className="landing-button landing-button-light" href={TENNIS_APP_URL}>
              Open tennis analysis <ArrowIcon />
            </a>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <a className="landing-brand" href="#top">
          <span className="landing-mark">e</span>
          <span>Evolve <b>Sports</b></span>
        </a>
        <p>Thoughtful motion intelligence for athletes in progress.</p>
        <p>© {new Date().getFullYear()} Evolve Sports</p>
      </footer>
    </div>
  );
}
